/**
 * useLocationData.js
 * Fetches countries + states from free public APIs (no auth).
 * Results are cached in module-level maps to avoid repeat calls.
 */
import { useEffect, useState, useCallback } from "react";

const COUNTRIES_URL = "https://countriesnow.space/api/v0.1/countries/flag/images";
const STATES_URL = "https://countriesnow.space/api/v0.1/countries/states";

/* Module-level cache — persists across renders & re-mounts */
let countriesCache = null;   // null = not yet loaded
let statesCache = {};     // { "Malaysia": ["Selangor", ...] }
let countriesFetching = false;
let statesFetching = {};     // { "Malaysia": true }

/* ── Country list ── */
export function useCountries() {
    const [countries, setCountries] = useState(countriesCache || []);
    const [loading, setLoading] = useState(!countriesCache);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (countriesCache) { setCountries(countriesCache); setLoading(false); return; }
        if (countriesFetching) {
            /* Poll until cache is ready */
            const t = setInterval(() => {
                if (countriesCache) { setCountries(countriesCache); setLoading(false); clearInterval(t); }
            }, 100);
            return () => clearInterval(t);
        }

        countriesFetching = true;
        fetch(COUNTRIES_URL)
            .then((r) => { if (!r.ok) throw new Error("Failed to load countries"); return r.json(); })
            .then((res) => {
                /* countriesnow.space returns { data: [{ name, flag, Iso2, Iso3 }] } */
                const list = (res.data || [])
                    .map((c) => ({
                        name: c.name,
                        flag: c.flag || "",
                        code: c.Iso2,
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name));
                countriesCache = list;
                countriesFetching = false;
                setCountries(list);
                setLoading(false);
            })
            .catch((e) => { setError(e.message); setLoading(false); countriesFetching = false; });
    }, []);

    return { countries, loading, error };
}

/* ── States for a given country ── */
export function useStates(countryName) {
    const [states, setStates] = useState(statesCache[countryName] || []);
    const [loading, setLoading] = useState(!statesCache[countryName] && !!countryName);
    const [error, setError] = useState(null);

    const load = useCallback((name) => {
        if (!name) { setStates([]); setLoading(false); return; }
        if (statesCache[name]) { setStates(statesCache[name]); setLoading(false); return; }
        if (statesFetching[name]) {
            const t = setInterval(() => {
                if (statesCache[name]) { setStates(statesCache[name]); setLoading(false); clearInterval(t); }
            }, 100);
            return () => clearInterval(t);
        }

        statesFetching[name] = true;
        setLoading(true);
        setError(null);

        fetch(STATES_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ country: name }),
        })
            .then((r) => r.json())
            .then((data) => {
                /* CountriesNow returns { data: { states: [{ name, state_code }] } } */
                const raw = data?.data?.states || [];
                const names = raw.map((s) => s.name).filter(Boolean).sort();
                statesCache[name] = names;
                statesFetching[name] = false;
                setStates(names);
                setLoading(false);
            })
            .catch((e) => {
                statesCache[name] = [];
                statesFetching[name] = false;
                setError(e.message);
                setLoading(false);
            });
    }, []);

    useEffect(() => { load(countryName); }, [countryName, load]);

    return { states, loading, error };
}

/* ── Postcode label by country ── */
const POSTCODE_LABELS = {
    US: "ZIP Code", CA: "Postal Code", GB: "Postcode",
    AU: "Postcode", MY: "Postcode", IN: "PIN Code",
    DE: "PLZ", FR: "Code Postal", JP: "Postal Code",
    CN: "邮政编码", BR: "CEP", SG: "Postal Code",
};

export function getPostcodeLabel(countryCode) {
    return POSTCODE_LABELS[countryCode] || "Postcode / ZIP";
}